import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ScrollText, Trophy } from "lucide-react";
import { Section, Eyebrow, Lede } from "../components/site/Section";
import { authApi } from "../lib/auth";
import { NATION_BY_SLUG, RARITY_STYLES } from "../lib/archive";

const RARITY_BORDER = { C: "#8C5A2B", B: "#B8B8B8", A: "#3F7BD0", S: "#C5A059" };

export default function KeeperProfilePage() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await authApi.get(`/keeper/${encodeURIComponent(username)}`);
        setData(res.data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "no_such_keeper" : "load_error");
      } finally {
        setLoaded(true);
      }
    })();
  }, [username]);

  if (!loaded) return null;

  if (error || !data) {
    return (
      <div data-testid="keeper-not-found">
        <Section className="pt-16 md:pt-28">
          <Eyebrow>Keeper Profile</Eyebrow>
          <h1 className="font-display text-4xl md:text-5xl text-[#EAE0D5] mt-4">No keeper by that name.</h1>
          <p className="text-sm text-[#A39E99] mt-3 max-w-md">
            The archive holds no record of <span className="text-[#EAE0D5]">{username}</span>.
            Perhaps the spelling differs, or perhaps they have yet to claim their sigil.
          </p>
          <div className="mt-7">
            <Link to="/leaderboard" className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[#C5A059] hover:underline">
              See the leaderboard <ArrowRight size={14} />
            </Link>
          </div>
        </Section>
      </div>
    );
  }

  const nationInfo = NATION_BY_SLUG[data.claimed_nation];
  const pct = data.total ? Math.round((data.unlocks / data.total) * 100) : 0;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const onShare = async () => {
    const text = `${data.username} has recovered ${data.unlocks} of ${data.total} records from The Bakhtak Archive.`;
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share({ title: `${data.username} — Bakhtak Keeper`, text, url: shareUrl }); return; } catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(`${text} ${shareUrl}`); } catch { /* no-op */ }
  };

  return (
    <div data-testid="keeper-profile-page">
      <Section testId="keeper-hero" className="pt-12 md:pt-24">
        <Eyebrow>Keeper Profile</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-4">
          <span className="italic font-light" style={{ color: nationInfo?.accent || "#C5A059" }}>
            {data.username}
          </span>
        </h1>
        {nationInfo && (
          <p className="mt-3 text-xs tracking-[0.28em] uppercase" style={{ color: nationInfo.accent }}>
            Claimed by {nationInfo.name}
          </p>
        )}
        <Lede>
          Below: every record this keeper has recovered, sealed under their sigil.
        </Lede>

        <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-2xl">
          <div className="bg-[#0A0A0A] border border-white/5 p-5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99]">Records</div>
            <div className="flex items-baseline gap-2 mt-2">
              <div className="font-display text-5xl text-[#C5A059] tabular-nums" data-testid="keeper-count">{data.unlocks}</div>
              <div className="font-display text-xl text-[#737373]">/ {data.total}</div>
            </div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/5 p-5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99]">Completion</div>
            <div className="font-display text-5xl text-[#EAE0D5] tabular-nums mt-2">{pct}%</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/5 p-5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99]">Joined</div>
            <div className="font-display text-lg text-[#EAE0D5] mt-2">
              {data.joined_at ? new Date(data.joined_at).toLocaleDateString() : "—"}
            </div>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap gap-3">
          <button
            onClick={onShare}
            data-testid="keeper-share-btn"
            className="inline-flex items-center gap-2 border border-[#C5A059]/40 text-[#C5A059] px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase hover:bg-[#C5A059]/10"
          >
            Share this profile
          </button>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase hover:bg-[#D4AF68]"
            data-testid="keeper-join-cta"
          >
            <Trophy size={13} /> Start your own archive
          </Link>
        </div>
      </Section>

      <Section testId="keeper-grid" className="pt-0 pb-24">
        <div className="ornate-rule text-[10px] tracking-[0.4em] uppercase text-[#C5A059] mb-7">
          <span>Recovered Records</span>
        </div>
        {data.tiles.length === 0 ? (
          <p className="text-sm text-[#737373]">No records under this sigil yet.</p>
        ) : (
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-testid="keeper-tiles">
            {data.tiles.map((c, idx) => {
              const border = RARITY_BORDER[c.rarity] || RARITY_BORDER.C;
              const rstyle = RARITY_STYLES[c.rarity] || RARITY_STYLES.C;
              const nation = NATION_BY_SLUG[c.nation];
              return (
                <motion.li
                  key={c.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: Math.min(0.4, idx * 0.02) }}
                  className="bg-[#0A0A0A] border-2 overflow-hidden"
                  style={{ borderColor: `${border}55`, boxShadow: `0 0 30px -18px ${border}` }}
                >
                  <div className="aspect-[3/4] bg-[#050505] overflow-hidden">
                    {c.image && (
                      <img
                        src={c.image}
                        alt={c.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                        style={{ objectPosition: c.image_position || "center" }}
                      />
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-[9px] tracking-[0.22em] uppercase" style={{ color: rstyle.color }}>{rstyle.label}</div>
                    <div className="font-display text-sm text-[#EAE0D5] mt-1 leading-tight">{c.name}</div>
                    <div className="text-[9px] tracking-[0.2em] uppercase text-[#737373]">{nation?.name || c.nation}</div>
                  </div>
                </motion.li>
              );
            })}
          </ul>
        )}
      </Section>
    </div>
  );
}
